import { useEffect, useMemo, useState } from "react";
import { fetchComplianceStatus, type ComplianceRate, type ShiftCoverage } from "../../services/compliance";
import { useTranslation, type LangCode } from "../../i18n";

type TFn = (key: string, fallback?: string) => string;
import { Activity, AlertTriangle, MapPin, ShieldCheck, Info, Users, Clock, CheckCircle2, XCircle } from "lucide-react";
import { HospitalMap } from "../../components/kiosk/HospitalMap";
import { useHospitalDashboard } from "../../hooks/useHospitalDashboard";
import { useKioskScans } from "../../hooks/useKioskScans";
import { cn } from "../../lib/utils";
import { useAuthStore } from "../../state/auth";
import { HospitalSelector } from "../../components/dashboard/HospitalSelector";
import { useStickyHospital } from "../../hooks/useStickyHospital";

export const DashboardHome = () => {
  const { t, lang } = useTranslation();
  const { user } = useAuthStore();
  // Default to "all my hospitals" (undefined) for everyone. The data endpoints
  // scope by the authenticated user's role, so for a SUPERVISOR this aggregates
  // exactly their assigned hospitals; they can drill into one via the selector.
  const [selectedHospitalId, setSelectedHospitalId] = useStickyHospital();
  const { data, loading, error, lastUpdated } = useHospitalDashboard(10000, selectedHospitalId);
  const { scans } = useKioskScans(10000, undefined, selectedHospitalId);

  // Compliance figures come from the same engine as the Compliance page:
  // the 2-hour rate (checkpoints scanned in the last 2 hours) and the current
  // shift's coverage (checkpoints scanned at least once since the shift began).
  const [compliance, setCompliance] = useState<{ rate: ComplianceRate | null; shift: ShiftCoverage | null } | null>(null);
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const res = await fetchComplianceStatus({ hospitalId: selectedHospitalId, status: "overdue", limit: 1 });
        if (alive) setCompliance({ rate: res.rate ?? null, shift: res.shift ?? null });
      } catch {
        if (alive) setCompliance((c) => c ?? { rate: null, shift: null });
      }
    };
    void load();
    const id = window.setInterval(() => {
      if (document.visibilityState === "visible") void load();
    }, 60_000);
    return () => {
      alive = false;
      window.clearInterval(id);
    };
  }, [selectedHospitalId]);

  const isSupervisor = user?.role === "SUPERVISOR" || user?.role === "DIRECTOR" || user?.role === "ADMIN" || user?.role === "Administrator";
  const isAdmin = user?.role === "ADMIN" || user?.role === "Administrator";
  const isDirector = user?.role === "DIRECTOR";
  // SUPERVISOR now gets the (role-scoped) hospital selector too, so multi-hospital
  // supervisors can switch between their assigned hospitals.
  const canSelectHospital = isAdmin || isDirector || user?.role === "SUPERVISOR";

  const rolePrefix = isDirector ? t("role.director", "Director") : isSupervisor ? t("role.supervisor", "Supervisor") : "";
  const rawHospitalName = data?.hospital_name || data?.hospital_id || t("home.hospitalSecurity", "Hospital Security");
  // The dashboard summary API returns the aggregated label as a raw data value
  // ("All Hospitals (Aggregated)"), so translate it to the active locale here.
  const hospitalName =
    rawHospitalName === "All Hospitals (Aggregated)"
      ? t("hospitals.allAggregated", "All Hospitals (Aggregated)")
      : rawHospitalName;

  const headerSubtitle = t("home.headerSubtitle", "Real-time patrol verification and hub upload status");

  if (error) {
    return (
      <div className="p-6">
        <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          {isSupervisor ? (
            <h1 className="text-2xl font-bold text-gray-900 leading-tight">
              {t("home.roleDashboard", "{role} Dashboard:").replace("{role}", rolePrefix)}
              <br />
              <span className="text-blue-900">{hospitalName}</span>
            </h1>
          ) : (
            <h1 className="text-2xl font-semibold text-gray-900">{t('dashboard.title')}</h1>
          )}
          <p className="mt-1 text-sm text-gray-600">{headerSubtitle}</p>
          <p className="text-xs text-gray-500">{t("home.lastUpdated", "Last updated:")} {lastUpdated ? new Intl.DateTimeFormat(lang, { hour: "numeric", minute: "2-digit", second: "2-digit" }).format(new Date(lastUpdated)) : "—"}</p>
        </div>
        {canSelectHospital && (
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-gray-700">{t("home.viewingHospital", "Viewing Hospital:")}</span>
            <HospitalSelector
              selectedHospitalId={selectedHospitalId}
              onHospitalChange={setSelectedHospitalId}
            />
          </div>
        )}
      </header>

      <section className="grid grid-cols-1 gap-3 md:gap-4 sm:grid-cols-3">
        <MetricCard
          title={t("home.recentScans7Days", "Recent Scans (7&nbsp;days)")}
          icon={Activity}
          tooltip={t("home.recentScansTooltip", "Total number of patrol scans successfully uploaded and verified in the last 7 days.")}
          value={loading ? "…" : (data?.metrics.total_scans_today.toString() ?? "0")}
        />
        <MetricCard
          title={t("home.totalCheckpoints", "Total Checkpoints")}
          icon={MapPin}
          tooltip={t("home.totalCheckpointsTooltip3", "All QR checkpoints on record at the selected hospital(s). Active = checkpoints in service and counted toward compliance — this matches the Checkpoint Directory and the Checkpoint Inventory spreadsheet (966 active across all hospitals as of Oct 7, 2026). Unregistered = retired or duplicate checkpoints removed from service; their scan history is kept.")}
          value={loading ? "…" : String(data?.metrics.total_location_count ?? data?.metrics.location_count ?? 0)}
          sub={
            loading || !data
              ? undefined
              : t("home.checkpointsActiveSub2", "{a} active · {o} unregistered")
                  .replace("{a}", data.metrics.location_count.toLocaleString())
                  .replace("{o}", Math.max(0, (data.metrics.total_location_count ?? data.metrics.location_count) - data.metrics.location_count).toLocaleString())
          }
        />
        <MetricCard
          title={t("home.complianceRate2h", "Compliance Rate (2-hour)")}
          icon={ShieldCheck}
          tooltip={t("home.complianceRate2hTooltip", "Share of active checkpoints scanned in the last 2 hours. Overdue checkpoints and checkpoints with no scan on record count as non-compliant. Same figure as the Compliance page.")}
          value={!compliance ? "…" : compliance.rate?.rate != null ? `${compliance.rate.rate.toFixed(1)}%` : "—"}
        />
      </section>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <section className="space-y-6">
          <ShiftCoverageCard shift={compliance?.shift ?? null} loading={!compliance} t={t} lang={lang} />
          <MissedCheckpointsCard data={data} loading={loading} t={t} />
        </section>

        <section className="space-y-6">
          <UploadsByShiftCard uploads={data?.recent_uploads ?? []} loading={loading} t={t} lang={lang} />
        </section>
      </div>
    </div>
  );
};

/**
 * Current-shift coverage: how many active checkpoints have been scanned at
 * least once since the shift began (Day 6am–2pm, Evening 2pm–10pm, Night
 * 10pm–6am, Eastern Time). Replaces the old "Recent Patrol Activity" card, whose
 * rate divided 7 days of scans by the checkpoint count and so read 100%.
 */
function ShiftCoverageCard({ shift, loading, t, lang }: { shift: ShiftCoverage | null; loading: boolean; t: TFn; lang: LangCode }) {
  const timeFmt = new Intl.DateTimeFormat(lang, { hour: "numeric", minute: "2-digit" });
  const shiftName = shift
    ? t(`home.shift.${shift.key}`, shift.label)
    : t("home.currentShift", "Current shift");
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <header className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{t("home.shiftCoverage", "Current Shift Coverage")}</h3>
          {shift && (
            <p className="text-xs text-gray-500">
              {shiftName} · {timeFmt.format(new Date(shift.start))} – {timeFmt.format(new Date(shift.end))}
            </p>
          )}
        </div>
        <CheckCircle2 className="h-5 w-5 text-emerald-500" />
      </header>
      <div className="space-y-4">
        {loading ? (
          <p className="text-sm text-gray-500">{t("home.loadingComplianceData", "Loading compliance data...")}</p>
        ) : !shift || shift.total === 0 ? (
          <p className="text-sm text-gray-500 italic">{t("home.noCheckpointsConfigured", "No checkpoints configured for this hospital.")}</p>
        ) : (
          <>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">{t("home.checkpointsScannedThisShift", "Checkpoints scanned this shift")}</span>
              <span className="font-semibold text-gray-900">
                {shift.scanned.toLocaleString(lang)} / {shift.total.toLocaleString(lang)}
              </span>
            </div>
            {/* Square-ended bar, same treatment as the Live Activity bar plot. */}
            <div className="relative h-4 w-full bg-gray-200" aria-hidden>
              <div
                className="absolute inset-y-0 left-0 bg-emerald-500 transition-all duration-1000"
                style={{ width: `${Math.max(0, Math.min(100, shift.rate ?? 0))}%` }}
              />
            </div>
            <p className="text-xs text-gray-500 italic font-medium">
              {t("home.shiftRateValue", "Shift rate: {n}% · {m} not yet scanned this shift")
                .replace("{n}", (shift.rate ?? 0).toFixed(1))
                .replace("{m}", shift.not_scanned.toLocaleString(lang))}
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function MissedCheckpointsCard({ data, loading, t }: { data: any; loading: boolean; t: TFn }) {
  const locations = data?.locations ?? [];
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <header className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">{t("home.registeredCheckpoints", "Registered Checkpoints")}</h3>
        <MapPin className="h-5 w-5 text-indigo-500" />
      </header>
      <div className="space-y-3">
        {loading ? (
          <p className="text-sm text-gray-500">{t("home.loadingCheckpoints", "Loading checkpoints...")}</p>
        ) : locations.length === 0 ? (
          <p className="text-sm text-gray-500 italic">{t("home.noCheckpointsConfigured", "No checkpoints configured for this hospital.")}</p>
        ) : (
          <>
            {locations.slice(0, 3).map((loc: any) => (
              <div key={loc.id} className="flex items-center justify-between p-2 rounded-lg bg-indigo-50 border border-indigo-100">
                <div>
                  <p className="text-sm font-medium text-indigo-900">{loc.name}</p>
                  <p className="text-xs text-indigo-700">{t("home.buildingFloor", "{building} - Floor {floor}").replace("{building}", loc.building ?? "").replace("{floor}", String(loc.floor ?? ""))}</p>
                </div>
                <MapPin className="h-4 w-4 text-indigo-600" />
              </div>
            ))}
            {locations.length > 3 && (
              <p className="text-xs text-center text-gray-500 mt-2">
                {t("home.moreCheckpoints", "+ {n} more checkpoints").replace("{n}", String(locations.length - 3))}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function UploadsByShiftCard({ uploads, loading, t, lang }: { uploads: any[]; loading: boolean; t: TFn; lang: LangCode }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <header className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">{t("home.uploadsByShift", "Uploads by Shift")}</h3>
        <Users className="h-5 w-5 text-indigo-500" />
      </header>
      <div className="space-y-3">
        {loading ? (
          <p className="text-sm text-gray-500">{t("home.loadingUploads", "Loading uploads...")}</p>
        ) : uploads.length === 0 ? (
          <p className="text-sm text-gray-500 italic">{t("home.noUploadsToday", "No uploads recorded today.")}</p>
        ) : uploads.map((upload) => (
          <div key={upload.id} className="flex items-center justify-between p-3 rounded-lg border border-gray-100 hover:bg-gray-50 transition-colors">
            <div className="flex items-center gap-3">
              <div className={cn(
                "p-2 rounded-full",
                upload.cleared_confirmed ? "bg-emerald-100" : "bg-indigo-100"
              )}>
                <ShieldCheck className={cn(
                  "h-4 w-4",
                  upload.cleared_confirmed ? "text-emerald-600" : "text-indigo-600"
                )} />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">{t("home.scanner", "Scanner: {serial}").replace("{serial}", upload.serial_number ?? "")}</p>
                <p className="text-xs text-gray-500">{t("home.hub", "Hub: {hub}").replace("{hub}", upload.hub_identifier ?? "")} • {new Intl.DateTimeFormat(lang, { hour: "numeric", minute: "2-digit", second: "2-digit" }).format(new Date(upload.uploaded_at))}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm font-semibold text-gray-900">{t("home.scansCount", "{n} scans").replace("{n}", String(upload.scan_count))}</p>
              <p className={cn(
                "text-[10px] uppercase font-bold tracking-wider",
                upload.cleared_confirmed ? "text-emerald-600" : "text-amber-600"
              )}>
                {upload.cleared_confirmed ? t("home.cleared", "CLEARED") : t("home.pendingClear", "PENDING CLEAR")}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MetricCard({ title, icon: Icon, tooltip, value, sub }: { title: string; icon: any; tooltip: string; value: string; sub?: string }) {
  return (
    <div className="group relative rounded-xl border border-gray-200 bg-white p-5 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex flex-col gap-3">
        <div className="flex items-start justify-between text-gray-500 h-10">
          <div className="flex items-center gap-2">
            <Icon className="h-5 w-5 text-indigo-500 shrink-0" />
            <span className="text-sm font-semibold uppercase tracking-wider leading-tight" dangerouslySetInnerHTML={{ __html: title }} />
          </div>
          <div className="relative pt-0.5">
            <Info className="h-4 w-4 cursor-help text-gray-400 hover:text-indigo-400" />

            {/* Custom Tooltip */}
            <div className="pointer-events-none absolute bottom-full right-0 mb-2 w-64 origin-bottom-right scale-75 rounded-lg bg-gray-900 px-3 py-2 text-xs font-medium text-white opacity-0 shadow-xl transition-all duration-200 group-hover:scale-100 group-hover:opacity-100 z-50">
              {tooltip}
              {/* Tooltip Arrow */}
              <div className="absolute right-1 top-full h-2 w-2 -translate-y-1 rotate-45 bg-gray-900" />
            </div>
          </div>
        </div>
        <div className="flex items-baseline justify-between min-w-0">
          <span className="text-2xl font-bold text-gray-900 truncate" title={value}>{value}</span>
        </div>
        {sub && <p className="-mt-2 text-sm font-medium text-emerald-700">{sub}</p>}
      </div>
    </div>
  );
}
